import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListSubheader from "@mui/material/ListSubheader";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ExpandLessIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ArrowRight";
import GroupIcon from "@mui/icons-material/Group";
import DashboardIcon from "@mui/icons-material/ViewComfy";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import FolderIcon from "@mui/icons-material/Folder";
import HelpIcon from "@mui/icons-material/Help";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";
import { grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";

import _last from "lodash/last";
import _isEqual from "lodash/isEqual";

import ISXUtils, {
  DashboardsContext,
  DRAWER_WIDTH,
  APP_BAR_HEIGHT,
} from "services/Utils";

const DEFAULT_DASHBOARD_TITLE = "UNNAMED DASHBOARD";
const DEFAULT_DASHBOARD_PATH_LABEL = "UNNAMED";

const DashboardListItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'viewed',
})(({ theme, viewed }) => ({
  "&.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, 0.4),
  },
  "&.Mui-selected:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.6),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
  },
  paddingTop: "0.125rem",
  paddingBottom: "0.125rem",
  paddingLeft: "0.5rem",
  backgroundColor: viewed ? "rgba(0, 0, 0, 0.08)" : "inherit",
  selected: {},
}));

const NodeIcon = (props) => {
  const NODE_ICONS_BY_TYPE = {
    folder: FolderIcon,
    "special-folder": FolderSpecialIcon,
    org: GroupIcon,
    dashboard: DashboardIcon,
  };
  const { type, ...rest } = props;
  const NodeTypeIcon = NODE_ICONS_BY_TYPE[type] ?? HelpIcon;
  return <NodeTypeIcon {...rest} />;
};

const _generateNodePath = (node) => {
  const path = [];
  while (node.parent) {
    path.push(node.parent.label);
    node = node.parent;
  }
  path.reverse();
  return path;
};

const NODE_TYPE_SORT_ORDERS = {
  org: 0,
  "special-folder": 1,
  folder: 2,
  dashboard: 3,
};
const _dashboardNodesCompare = (a, b) => {
  const odiff = NODE_TYPE_SORT_ORDERS[a.type] - NODE_TYPE_SORT_ORDERS[b.type];
  return odiff || ISXUtils.strcasecmp(a.label, b.label);
};

const _createDashboardHiearchy = (
  dashboards,
  dashboardNodes,
  topParent = null
) => {
  const _flattenHierarchyNodes = (nodes) => {
    nodes = Object.values(nodes);
    nodes.forEach(
      (node) => (node.subnodes = _flattenHierarchyNodes(node.subnodes ?? {}))
    );
    return nodes.sort(_dashboardNodesCompare);
  };

  let dashboardHierarchy = {};
  dashboards.forEach((dashboard) => {
    let nodes = dashboardHierarchy;
    const title = dashboard.title ?? "";
    const path = title.split(".");
    let parent = null;
    path.slice(0, -1).forEach((el, idx) => {
      // trim leading and trailing whitespace, and replace all
      // remaining multiple whitespace with single space
      const label =
        el.trim().replace(/\s+/g, " ") || DEFAULT_DASHBOARD_PATH_LABEL;
      const id = `${topParent?.id ?? ""}.${path.slice(0, idx + 1).join(".")}`;
      let next = nodes[id];
      if (!next) {
        next = nodes[id] = {
          id,
          label,
          type: "folder",
          subnodes: {},
          parent,
        };
      }
      parent = next;
      nodes = next.subnodes;
    });
    const id = `${topParent?.id ?? ""}.${path.slice(0, -1).join(".")}.${dashboard.guuid
      }`;
    const dnode = (nodes[id] = {
      id,
      label: _last(path) || DEFAULT_DASHBOARD_TITLE,
      type: "dashboard",
      entity: dashboard,
      parent,
    });
    (dashboardNodes[dashboard.guuid] ??= []).push(dnode);
  });
  dashboardHierarchy = _flattenHierarchyNodes(dashboardHierarchy);
  dashboardHierarchy.forEach((subnode) => (subnode.parent = topParent));
  return dashboardHierarchy;
};

const SEARCH_OPTION_SORT_ORDERS = {
  org: 0,
  "special-folder": 1,
  folder: 2,
  dashboard: 3,
};

const _dashboardSearchOptionsCompare = (a, b) => {
  const tdiff =
    SEARCH_OPTION_SORT_ORDERS[a.type] - SEARCH_OPTION_SORT_ORDERS[b.type];
  if (tdiff === 0) {
    return ISXUtils.strcasecmp(a.label, b.label);
  } else {
    return tdiff;
  }
};

const DashboardsNestedList = ({
  node,
  dashboardId,
  showCounts,
  closeDrawer,
  disabled,
}) => {
  const { expanded, setExpanded, selected, setSelected, viewed } =
    useContext(DashboardsContext);

  const toggle = () => {
    setExpanded((expanded) => {
      const newExpanded = new Set(expanded);
      if (newExpanded.has(node.id)) {
        newExpanded.delete(node.id);
      } else {
        newExpanded.add(node.id);
      }
      return newExpanded;
    });
  };

  const _getNodeLabel = (node) => {
    let label = node.label;
    if (showCounts && node.type !== "dashboard") {
      label += ` (${node.count || 0})`;
    }
    return label;
  };

  const open = expanded.has(node.id);

  return (
    <>
      <DashboardListItem
        dense
        onClick={() => {
          setSelected(new Set([node.id]));
          toggle();
        }}
        selected={selected.has(node.id)}
        viewed={viewed.has(node.id)}
        disabled={disabled}
      >
        <Box
          component={"span"}
          sx={{
            display: "flex",
            color: "rgba(0, 0, 0, 0.54)",
            opacity: node.subnodes?.length > 0 ? 1 : 0,
          }}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <ListItemIcon>
          <NodeIcon type={node.type} fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={_getNodeLabel(node)}
          primaryTypographyProps={{
            sx: {
              fontWeight: "bold",
              fontSize: "0.6875rem",
            },
          }}
        />
      </DashboardListItem>
      <Collapse
        in={open}
        mountOnEnter={true}
        timeout="auto"
        sx={{
          paddingLeft: 4,
        }}
      >
        <DashboardsList
          dashboardId={dashboardId}
          nodes={node.subnodes}
          showCounts={showCounts}
          closeDrawer={closeDrawer}
          disabled={disabled}
        />
      </Collapse>
    </>
  );
};

const DashboardsList = ({
  dashboardId,
  nodes,
  showCounts,
  closeDrawer,
  disabled,
}) => {
  const { setLoading, selected, setSelected, viewed } =
    useContext(DashboardsContext);

  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  return (
    <List sx={{ paddingY: 0 }}>
      {nodes.map((cnode) => {
        if (
          cnode.type === "folder" ||
          cnode.type === "special-folder" ||
          cnode.type === "org"
        ) {
          return (
            <DashboardsNestedList
              key={cnode.id}
              dashboardId={dashboardId}
              node={cnode}
              showCounts={showCounts}
              closeDrawer={closeDrawer}
              disabled={disabled}
            />
          );
        } else {
          const dashboard = cnode.entity;
          return (
            <DashboardListItem
              key={dashboard.guuid}
              dense
              selected={selected.has(cnode.id)}
              viewed={viewed.has(cnode.id)}
              onClick={() => {
                if (dashboard.guuid !== dashboardId) {
                  setLoading(true);
                  navigate("/dashboard/" + dashboard.guuid);
                }
                setSelected(new Set([cnode.id]));
                if (isSmallerScreen) {
                  closeDrawer();
                }
              }}
              disabled={disabled}
            >
              <ListItemIcon>
                <NodeIcon type={cnode.type} fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={cnode.label}
                primaryTypographyProps={{
                  sx: {
                    fontSize: "0.6875rem",
                  },
                }}
              />
            </DashboardListItem>
          );
        }
      })}
    </List>
  );
};

const GroupDashboardSearch = (props) => {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [labelText, setLabelText] = useState();
  const navigate = useNavigate();

  const { dashboardId, options, open, setOpen, closeDrawer, label } = props;
  const { setLoading, setExpanded, setSelected } =
    useContext(DashboardsContext);

  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setLabelText(label);
  }, [label]);

  return (
    <Box height={56} display="flex" alignItems="center">
      <Autocomplete
        open={open}
        fullWidth
        disableClearable
        popupIcon={null}
        size="small"
        blurOnSelect
        value={value}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setOpen(newInputValue.length > 0)
          setInputValue(newInputValue);
        }}
        options={options}
        getOptionKey={(option) => option.id}
        getOptionLabel={(option) => option.label}
        groupBy={(option) =>
          option.type === "dashboard" ? "Dashboards" : "Groups"
        }
        onClose={() => setOpen(false)}
        renderGroup={(params) => {
          return [
            <ListSubheader
              key={params.key}
              sx={{
                color: "primary.main",
                fontSize: "0.75rem",
                fontWeight: "bolder",
                lineHeight: "1rem",
                marginY: 0.5,
                paddingLeft: 2,
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                backgroundColor: "background.paper",
              }}
            >
              {params.group}
            </ListSubheader>,
            params.children,
          ];
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box
              key={key}
              component="li"
              sx={{
                display: "flex",
                color: "text.secondary",
                paddingX: 2,
                paddingY: 0.75,
              }}
              {...optionProps}
            >
              <NodeIcon
                type={option.type}
                sx={{ fontSize: "1rem", marginRight: 1 }}
              />
              <Typography
                component={"span"}
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: option.type === "dashboard" ? 400 : 700,
                  color: "rgba(0, 0, 0, 0.87)",
                }}
              >
                {option.label}
                {option.path?.length > 0 && option.dcount > 1 && (
                  <span style={{ color: grey[400], fontStyle: "italic" }}>
                    {" ("}
                    {option.path.map((part, index) => (
                      <React.Fragment key={`${part}-${index}`}>
                        {part}
                        {index < option.path.length - 1 && (
                          <ArrowForwardIcon
                            sx={{
                              fontSize: "0.5rem",
                              paddingX: 0.25,
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                    {")"}
                  </span>
                )}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onFocus={() => setLabelText(`Searching ${label}...`)}
            onBlur={() => setLabelText(label)}
            label={
              <Box display="flex" alignItems="center" columnGap={1} marginTop={0.25}>
                <SearchIcon sx={{ fontSize: "1.25rem" }} />
                {labelText}
              </Box>
            }
            variant="filled"
            margin="none"
            inputProps={{
              ...params.inputProps,
            }}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              style: { backgroundColor: "inherit" },
            }}
            InputLabelProps={{
              style: {
                fontSize: "1rem",
              },
            }}
          />
        )}
        onChange={(_, option) => {
          if (option) {
            setSelected(new Set([option.id]));
            setExpanded((expanded) => {
              const newExpanded = new Set(expanded);
              let cur = option;
              while (cur.parent) {
                newExpanded.add(cur.parent.id);
                cur = cur.parent;
              }
              return newExpanded;
            });
            setValue(null);
            setInputValue("");
            if (isSmallerScreen) {
              closeDrawer();
            }
            if (
              option.type === "dashboard" &&
              option.entity.guuid !== dashboardId
            ) {
              setLoading(true);
              navigate("/dashboard/" + option.entity.guuid);
            }
          }
        }}
      />
    </Box>
  );
};

const GroupsDashboardsNavDrawer = (props) => {
  const [searchOpen, setSearchOpen] = useState(false);
  // used to limit recomputations
  const [dashboardMetadata, setDashboardMetadata] = useState({});
  const initialized = useRef(false);

  const {
    expanded,
    setExpanded,
    setSelected,
    drawerOpen: open,
    setDrawerOpen: setOpen,
    setViewed,
    addDashboard,
  } = useContext(DashboardsContext);

  // const theme = useTheme();
  // const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
  const params = useParams();

  const user = props.user;

  // only need minimal db metadata, and only need to recompute if this metadata changed
  useEffect(() => {
    const newMeta = Object.entries(props.dashboards).reduce(
      (acc, [guuid, db]) => {
        acc[guuid] = { guuid, title: db.title, acu_groups: db.acu_groups };
        return acc;
      },
      {}
    );
    setDashboardMetadata((meta) => {
      return _isEqual(newMeta, meta) ? meta : newMeta;
    });
  }, [props.dashboards]);

  const {
    dashboardSearchOptions,
    dashboardNodes,
    smartFolders,
    orgHierarchy,
    label,
  } = useMemo(() => {
    // avoid computation if dialog not open
    if (!open) {
      return {
        dashboardSearchOptions: [],
        dashboardNodes: {},
        smartFolders: [],
        orgHierarchy: [],
        label: null,
      };
    }

    // get personal dashboards (owned by this user, not shared with any group
    // user is member of)
    const candidates = user.dashboards
      .map((guuid) => dashboardMetadata[guuid])
      .filter(Boolean);
    const user_orgs = new Set(user.orgs.map((org) => org.orgid));
    const personal = candidates.filter(
      (dashboard) =>
        dashboard && (dashboard.acu_groups?.length == 0 || !dashboard.acu_groups?.filter((org) => user_orgs.has(org)))
    );

    const dashboardNodes = {};

    const visibleOnlyToMeFolder = {
      id: "sf-personal",
      label: "Visible Only to Me",
      type: "special-folder",
      parent: null,
    };
    const personalDashboardHierarchy = _createDashboardHiearchy(
      personal,
      dashboardNodes,
      visibleOnlyToMeFolder
    );
    visibleOnlyToMeFolder.subnodes = personalDashboardHierarchy;
    const smartFolders = [visibleOnlyToMeFolder];

    const personalIds = new Set(personal.map((dashboard) => dashboard.guuid));

    let orgHierarchy = {};
    const _buildOrgNode = (org, parent, seen) => {
      if (seen.has(org.guuid)) {
        console.warn("cycle");
        return null;
      }
      seen.add(org.guuid);
      let node = orgHierarchy[org.guuid];
      if (node) {
        node.root &&= !parent;
        if (parent && !node.parent) {
          node.parent = orgHierarchy[parent];
        }
      } else {
        node = {
          id: `org-${org.guuid}`,
          label: org.name,
          type: "org",
          entity: org,
          root: !parent,
          parent: parent ? orgHierarchy[parent] : null,
        };
        orgHierarchy[org.guuid] = node;
        const suborgs =
          org.orgs
            ?.map((suborgId) => {
              const suborg = props.orgs[suborgId];
              return suborg && _buildOrgNode(suborg, org.guuid, seen);
            })
            .filter(Boolean) ?? [];
        const dashboards =
          org.dashboards
            ?.filter((dashboardId) => !personalIds.has(dashboardId))
            .map((dashboardId) => dashboardMetadata[dashboardId])
            .filter(Boolean) ?? [];
        const dashboardHierarchy = _createDashboardHiearchy(
          dashboards,
          dashboardNodes,
          node
        );
        node.subnodes = [
          ...suborgs.sort(_dashboardNodesCompare),
          ...dashboardHierarchy,
        ];
        seen.delete(org.guuid);
      }
      return node;
    };
    Object.values(props.orgs).forEach((org) => {
      _buildOrgNode(org, null, new Set());
    });
    orgHierarchy = Object.values(orgHierarchy)
      .filter((entry) => entry.root)
      .sort(_dashboardNodesCompare);

    let label = null;
    if (orgHierarchy.length === 1) {
      label = orgHierarchy[0].label ?? "My Organization";
      orgHierarchy = orgHierarchy[0].subnodes;
      orgHierarchy.forEach((node) => (node.parent = null));
    }

    const _getDashboardCounts = (node) => {
      if (node.type === "dashboard") {
        // don't need to store count on dashboard node
        return 1;
      } else {
        let count = 0;
        node.subnodes?.forEach((subnode) => {
          count += _getDashboardCounts(subnode);
        });
        node.count = count;
        return count;
      }
    };

    orgHierarchy.forEach((org) => {
      _getDashboardCounts(org, null, new Set());
    });

    const dashboardSearchOptions = [];
    const _generateSearchOptions2 = (nodes) => {
      nodes.forEach((node) => {
        dashboardSearchOptions.push(node);
        _generateSearchOptions2(node.subnodes);
      });
    };
    _generateSearchOptions2(personalDashboardHierarchy);
    _generateSearchOptions2(orgHierarchy);
    dashboardSearchOptions.sort(_dashboardSearchOptionsCompare);

    dashboardSearchOptions.forEach(
      (option) => (option.path = _generateNodePath(option))
    );
    const duplicateCounts = {};
    dashboardSearchOptions.forEach((option) => {
      const key = `${option.type}|${option.label}`;
      duplicateCounts[key] = (duplicateCounts[key] ?? 0) + 1;
    });
    dashboardSearchOptions.forEach((option) => {
      const key = `${option.type}|${option.label}`;
      option.dcount = duplicateCounts[key];
    });
    // console.log({
    //   dashboardSearchOptions,
    //   dashboardNodes,
    //   smartFolders,
    //   orgHierarchy,
    //   label,
    // });
    return {
      dashboardSearchOptions,
      dashboardNodes,
      smartFolders,
      orgHierarchy,
      label,
    };
  }, [open, user.dashboards, user.orgs, props.orgs, dashboardMetadata]);

  useEffect(() => {
    if (params.dashboardGuuid) {
      const nodes = dashboardNodes[params.dashboardGuuid];
      if (nodes?.length > 0) {
        if (!initialized.current) {
          initialized.current = true;
          setExpanded((expanded) => {
            const newExpanded = new Set(expanded);
            nodes.forEach((node) => {
              let cur = node;
              while (cur.parent) {
                newExpanded.add(cur.parent.id);
                cur = cur.parent;
              }
            });
            return newExpanded;
          });
        }
        setSelected(new Set(nodes.map((node) => node.id)));
      }
    }
  }, [dashboardNodes, params.dashboardGuuid, setExpanded, setSelected]);

  useEffect(() => {
    const nodes = dashboardNodes[params.dashboardGuuid];
    const viewed = new Set(nodes?.map((node) => node.id) ?? []);
    // "breadcrumbs" if viewed dashboard is not expanded
    nodes?.reduce((acc, node) => {
      let cur = node;
      let crumb = node;
      while (cur.parent) {
        if (!expanded.has(cur.parent.id)) {
          crumb = cur.parent;
        }
        cur = cur.parent;
      }
      acc.add(crumb.id);
      return acc;
    }, viewed);
    setViewed(viewed);
  }, [dashboardNodes, expanded, params.dashboardGuuid, setViewed]);

  const closeDrawer = () => setOpen(false);

  return (
    <Drawer
      PaperProps={{
        sx: {
          // width: isSmallerScreen ? "calc(100% - 44px)" : DRAWER_WIDTH,
          width: { xs: "calc(100% - 44px)", md: DRAWER_WIDTH },
          marginTop: APP_BAR_HEIGHT / 8,
          height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
          backgroundColor: "#f8f8f8",
          boxShadow: "0.25rem 0 0.5rem rgba(0, 0, 0, 0.125)",
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
      onClose={closeDrawer}
    >
      <Box overflow="auto" display="flex" flexDirection="column" height="100%">
        <Box>
          <GroupDashboardSearch
            options={dashboardSearchOptions}
            open={searchOpen}
            setOpen={setSearchOpen}
            dashboardId={props.dashboardId}
            closeDrawer={closeDrawer}
            label={label ?? "My Organizations"}
          />
          <Divider />
          <Button
            color="primary"
            onClick={() => {
              addDashboard();
              closeDrawer();
            }}
            sx={{
              fontSize: "0.825rem",
              marginLeft: 1,
              marginTop: 2,
              textTransform: "none",
              paddingY: 0,
            }}
          >
            + Create new dashboard
          </Button>
        </Box>
        <Box
          paddingBottom={1}
          paddingRight={1}
          flexShrink={0}
          overflow="auto"
          maxHeight="35%"
        >
          <List>
            <DashboardsList
              dashboardId={props.dashboardId}
              nodes={smartFolders}
              closeDrawer={closeDrawer}
              disabled={searchOpen}
            />
          </List>
        </Box>
        <Divider />
        <Box overflow="auto">
          {/* <DashboardTreeView nodes={orgHierarchy} /> */}
          <DashboardsList
            dashboardId={props.dashboardId}
            nodes={orgHierarchy}
            showCounts
            closeDrawer={closeDrawer}
            disabled={searchOpen}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default GroupsDashboardsNavDrawer;
